import NiceModal from '@ebay/nice-modal-react';
import { enqueueSnackbar } from 'notistack';
import { isNilOrEmptyString } from 'ramda-extension';
import { createAction } from 'redux-actions';

import { replaceData, resetForm } from '@gef-ui/form-core/stream/transfer/control';
import { getMessage } from '@gef-ui/localization/messages';

import { appendMode } from 'constants/general';
import MODALS from 'constants/modals';
import fetch from 'features/common/fetch';
import { resetErrorsByPage } from 'features/common/submitFormDialog/slice';
import guarantors from 'features/views/campaignSettings/guarantors';
import user from 'features/views/user';
import { generateCommonFetchActions } from 'utils';

import { EDIT_GUARANTOR_FORM_NAME } from '../campaignSettings/guarantors/form';
import * as types from './actionTypes';
import { getGuarantorsOfRegion } from './selectors';
import { queryParamsFactory } from './utils';

const addGuarantorOfRegion = createAction(types.ADD_GUARANTOR_OF_REGION, (guarantor) => ({ guarantor }));
const addGuarantors = createAction(types.ADD_GUARANTORS);
const appendGuarantors = createAction(types.APPEND_GUARANTORS, (guarantors) => ({ guarantors }));
const removeGuarantorOfRegion = createAction(types.REMOVE_GUARANTOR_OF_REGION, (id) => ({ id }));
const resetGuarantors = createAction(types.RESET_GUARANTORS);
const selectGuarantors = createAction(types.SELECT_GUARANTORS, (guarantorIds) => ({ guarantorIds }));
const setGuarantors = createAction(types.SET_GUARANTORS, (guarantors) => ({ guarantors }));
const setGuarantorsOfRegion = createAction(types.SET_GUARANTORS_OF_REGION, (guarantors) => ({ guarantors }));
const setRadioButton = createAction(types.SET_RADIO_BUTTON, (radio) => ({ radio }));
const reset = createAction(types.RESET, (guarantors) => ({ guarantors }));
const guarantorsEditHardReset = createAction(types.GUARANTORS_EDIT_HARD_RESET);

const url = fetch.constants.urls.campaignGuarantorCandidate;

const loadGuarantors =
	({ queryParams }, append = appendMode.SET) =>
	(dispatch) => {
		dispatch(
			fetch.actions.get(url, {
				takeLatest: true,
				queryParams,
				onSuccess: [
					(response) => () => {
						const data = response.body.data;
						if (!data || !Array.isArray(data)) return;
						dispatch(append === appendMode.APPEND ? appendGuarantors(data) : setGuarantors(data));
					},
				],
			}),
		);
	};

const { loadMore, loadPage, reload } = generateCommonFetchActions(loadGuarantors, url);

const organizationStructureChange =
	() =>
	async (dispatch, getState, { getFormData }) => {
		const formData = await getFormData(EDIT_GUARANTOR_FORM_NAME);
		if (!formData.agency) return dispatch(actions.resetGuarantors());
		const queryParams = queryParamsFactory(formData, 'select');
		dispatch(actions.loadGuarantors({ queryParams }));
	};

const removeAndReloadGuarantorOfRegion =
	(id) =>
	async (dispatch, getState, { getFormData }) => {
		const formData = await getFormData(EDIT_GUARANTOR_FORM_NAME);
		if (!isNilOrEmptyString(formData.agency)) dispatch(actions.reload());
		dispatch(actions.removeGuarantorOfRegion(id));
	};

const submitForm =
	() =>
	async (dispatch, getState, { getFormData }) => {
		const formData = await getFormData(EDIT_GUARANTOR_FORM_NAME);
		const queryParams = queryParamsFactory(formData, 'table');
		dispatch(actions.loadGuarantors({ queryParams }));
		dispatch(resetErrorsByPage({ page: EDIT_GUARANTOR_FORM_NAME }));
	};

const changeRadio =
	(radio) =>
	async (dispatch, getState, { sendControlNext, getFormData }) => {
		const formData = await getFormData(EDIT_GUARANTOR_FORM_NAME);

		dispatch(actions.setRadioButton(radio));
		sendControlNext(EDIT_GUARANTOR_FORM_NAME, resetForm());
		sendControlNext(
			EDIT_GUARANTOR_FORM_NAME,
			replaceData({
				data: {
					channel: formData.channel,
					region: formData.region,
				},
			}),
		);

		dispatch(fetch.actions.clearFetchStore(url));
		dispatch(resetErrorsByPage({ page: EDIT_GUARANTOR_FORM_NAME }));
	};

const resetCriteria =
	(channelId, region = {}) =>
	(dispatch, getState, { sendControlNext }) => {
		dispatch(actions.reset(region.guarantors));
		sendControlNext(EDIT_GUARANTOR_FORM_NAME, resetForm());
		sendControlNext(
			EDIT_GUARANTOR_FORM_NAME,
			replaceData({
				data: {
					channel: channelId,
					region: region.id,
				},
			}),
		);

		dispatch(fetch.actions.clearFetchStore(url));
		dispatch(resetErrorsByPage({ page: EDIT_GUARANTOR_FORM_NAME }));
	};

const saveGuarantors = (regionId, campaignId) => (dispatch, getState) => {
	const state = getState();
	const savedGuarantors = getGuarantorsOfRegion(state);
	dispatch(
		fetch.actions.put(fetch.constants.urls.campaignGuarantorRegionGuarantor, {
			replaceParams: { id: campaignId, regionId },
			body: savedGuarantors.map((g) => g.id),
			onSuccess: [
				() => () => {
					dispatch(user.actions.fetchAdditionalUserInfo());
					dispatch(guarantors.actions.setRegionGuarantors(regionId, savedGuarantors));
					NiceModal.hide(MODALS.edit_guarantor);
					enqueueSnackbar(getMessage('toastr:approveSaveGuarantors'), { variant: 'success' });
				},
			],
		}),
	);
};

const setInitialState =
	(channelId, regionId) =>
	(dispatch, getState, { sendControlNext }) => {
		const data = {
			channel: channelId,
			region: regionId,
		};
		sendControlNext(EDIT_GUARANTOR_FORM_NAME, replaceData({ data }));
	};

export const actions = {
	addGuarantorOfRegion,
	addGuarantors,
	appendGuarantors,
	removeGuarantorOfRegion,
	resetGuarantors,
	selectGuarantors,
	setGuarantors,
	setGuarantorsOfRegion,
	setRadioButton,
	reset,
	loadGuarantors,
	loadMore,
	loadPage,
	reload,
	organizationStructureChange,
	removeAndReloadGuarantorOfRegion,
	submitForm,
	changeRadio,
	resetCriteria,
	saveGuarantors,
	guarantorsEditHardReset,
	setInitialState,
};
